import { useSelector } from "react-redux";
import { useMemo } from "react";

const useBusinessPermissions = () => {
  const user = useSelector((state) => state.auth.user);
  const selectedBusinessId = useSelector((state) => state.auth.selectedBusinessId);
  const primaryLocationId = useSelector((state) => state.auth.primaryLocationId);
  const profileType = useSelector((state) => state.auth.profileType);

  const currentEmployee = useMemo(() => {
    if (profileType !== "business" || !selectedBusinessId || !user?.employee) {
      return null;
    }
    return (
      user.employee.find(
        (emp) =>
          emp?.businessId?._id === selectedBusinessId ||
          emp?._id === selectedBusinessId
      ) || null
    );
  }, [user, selectedBusinessId, profileType]);

  const isOwnBusiness = useMemo(
    () => currentEmployee?.isOwnBusiness === true,
    [currentEmployee]
  );

  const locationPermissions = useMemo(() => {
    if (isOwnBusiness || !currentEmployee?.locationPermissions) {
      return null;
    }
    const locationPerm = currentEmployee.locationPermissions.find(
      (perm) => perm.locationId === primaryLocationId
    );
    return locationPerm?.permissions || null;
  }, [currentEmployee, primaryLocationId, isOwnBusiness]);

  const hasPermission = (permissionKey) => {
    if (isOwnBusiness) return true;
    if (!locationPermissions) return false;
    return locationPermissions[permissionKey] === true;
  };

  const hasAnyPermission = (permissionKeys) => {
    if (isOwnBusiness) return true;
    if (!locationPermissions) return false;
    return permissionKeys.some((key) => locationPermissions[key] === true);
  };

  const hasAllPermissions = (permissionKeys) => {
    if (isOwnBusiness) return true;
    if (!locationPermissions) return false;
    return permissionKeys.every((key) => locationPermissions[key] === true);
  };

  const isViewOnly = () => {
    if (isOwnBusiness || !locationPermissions) return false;
    return locationPermissions.viewOnly === true;
  };

  const hasNoAccess = () => {
    if (isOwnBusiness || !locationPermissions) return false;
    return locationPermissions.viewNothing === true;
  };

  return {
    isOwnBusiness,
    locationPermissions,
    currentEmployee,
    primaryLocationId,
    selectedBusinessId,
    profileType,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isViewOnly,
    hasNoAccess,
    isBusinessProfile: profileType === "business",
  };
};

export default useBusinessPermissions;

